import { useState } from "react";
import { Candidate, QuestionType } from "../types";
import { DefaultQuestions, DefaultTopics } from "../consts";

export default function Questions(
{
  candidates,
  selectQuestionCallback,
  responding,
  selectedCandidate,
}:
{
  candidates: Candidate[];
  selectQuestionCallback: (question: string, type: QuestionType, candidate: Candidate, useExact: boolean, qId: number) => void;
  responding: number;
  selectedCandidate: Candidate | null;
}) {
  const [userQuestion, setUserQuestion] = useState("");
  const [userQuestionType, setUserQuestionType] = useState(QuestionType.Topic);
  const [useExactQuestion, setUseExactQuestion] = useState(false);

  const updateUserQuestion = (val: string) => {
    setUserQuestion(val);
    if (val.endsWith("?") || val.split(" ").length > 5) {
      setUserQuestionType(QuestionType.Question);
    } else {
      setUserQuestionType(QuestionType.Topic);
    }
  }

  const getCandidateStyle = (candidate: Candidate, qId: number) => {
    if (qId === responding) {
      if (candidate === selectedCandidate) {
        return candidate.styles.selected;
      } else {
        return "bg-gray-500";
      }
    } else {
      if (qId === 1 && !userQuestion) {
        return `bg-gray-500 ${candidates[0] === candidate ? "border-r md:border-r-0 md:border-b border-gray-200" : ""}`;
      } else {
        return `${candidate.styles.default} hover:${candidate.styles.hover}`;
      }
    }
  }

  const generateQuestion = (type: string) => {
    let question = "";
    if (type === "Question") {
      question = DefaultQuestions[Math.floor(Math.random() * DefaultQuestions.length)];
    } else {
      question = DefaultTopics[Math.floor(Math.random() * DefaultTopics.length)];
    }

    setUserQuestion(question);
    setUserQuestionType(type === "Question" ? QuestionType.Question : QuestionType.Topic);
  }

  const getQuestionCard = (body: string, type: QuestionType, id: number) => {
    return (
      <div className="group relative shadow md:shadow-lg flex rounded-lg w-fit">
        <div className={`h-full ${responding === id ? "hidden md:flex" : "hidden"} flex-col rounded-l-lg overflow-hidden ${responding === 0 ? "group-hover:md:flex" : "" }`}>
          {candidates.map(candidate => {
            return (
              <div
                key={candidate.name}
                className={`h-1/2 flex items-center text-sm font-medium text-white px-2 cursor-pointer
                      ${getCandidateStyle(candidate, id)}
                    `}
                onClick={() => responding === 0 && selectQuestionCallback(body, type, candidate, false, id)}
              >
                Ask {candidate.name}
              </div>
            )
          })}
        </div>
        <div
          className="flex flex-col w-[336px] min-w-[336px] py-2 px-4 bg-white rounded-lg hover:rounded-r-lg hover:rounded-l-none"
        >
          {type === QuestionType.Question && (
            <>
              <div className="text-sm font-medium text-fuchsia-800">Question</div>
              <div className="mt-0.5 text-md font-semibold text-slate-900">{body}</div>
            </>
          )}
          {type === QuestionType.Topic && (
            <>
              <div className="text-sm font-medium text-indigo-700">Topic</div>
              <div className="text-lg font-semibold text-slate-900">{body}</div>
            </>
          )}
          <div className="mt-2 w-full flex justify-end">
            <div className="text-xs font-medium text-blue-500">Generate with AI</div>
          </div>
        </div>
        
      </div>
    );
  }

  const getCustomQuestionCard = (id: number) => {
    return (
      <div className={`group relative shadow md:shadow-lg flex flex-col md:flex-row rounded-lg w-fit ${responding !== 0 ? "bg-gray-100" : ""}`}>
        <div className={`h-full ${responding === id ? "hidden md:flex" : "hidden"} flex-col rounded-l-lg overflow-hidden ${responding === 0 ? "group-hover:md:flex" : "" }`}>
          {candidates.map(candidate => {
            return (
              <div
                key={candidate.name}
                className={`h-1/2 flex items-center text-sm font-medium text-white px-2 cursor-pointer
                      ${getCandidateStyle(candidate, id)}
                    `}
                onClick={() => responding === 0 && userQuestion && selectQuestionCallback(userQuestion, userQuestionType, candidate, useExactQuestion, id)}
              >
                Ask {candidate.name}
              </div>
            )
          })}
        </div>
        <div
          className={`flex flex-col w-[336px] min-w-[336px] py-2 px-4 ${responding !== 0 ? "bg-gray-100" : "bg-white"} rounded-lg hover:rounded-r-lg hover:rounded-l-none`}
        >  
          {userQuestionType === QuestionType.Topic && <div className="text-sm font-medium text-indigo-700">Topic</div>}
          {userQuestionType === QuestionType.Question && <div className="text-sm font-medium text-fuchsia-800">Question</div>}
          <textarea
            placeholder="Enter a topic or question"
            className={`${userQuestionType === QuestionType.Topic ? "text-lg": "mt-0.5 text-md"}
                        ${responding !== 0 ? "bg-gray-100 rounded-lg text-gray-500" : "text-slate-900"}
                        font-semibold  p-0 border-none focus:outline-none focus:ring-0 resize-none`}
            onChange={(e) => updateUserQuestion(e.target.value)}
            disabled={responding !== 0}
            defaultValue={userQuestion}
            value={userQuestion}
            rows={userQuestionType === QuestionType.Topic ? 2 : 4}
          ></textarea>
          <div className="mt-2 w-full flex flex-col gap-2 md:gap-0 md:flex-row md:items-center justify-between">
            {responding === 0 && <div className="text-blue-500 text-xs font-medium md:hidden">AI will use this along with recent events and the candidates' background to generate a question.</div>}
            <div className="flex items-end gap-1.5">
              <div className="text-xs md:text-xs">Ask this question exactly</div>
              <input
                type="checkbox"
                title="Use this question exactly"
                className="rounded-md w-4 h-4 md:rounded md:w-3.5 md:h-3.5 text-blue-500 focus:ring-0 focus:ring-offset-0"
                onClick={() => setUseExactQuestion(!useExactQuestion)}
                disabled={responding !== 0}
              />
            </div>
            <div className="hidden md:block text-xs font-semibold text-blue-500">Ask a question</div>
          </div>
        </div>
        <div className="md:hidden flex w-full rounded-b-lg overflow-hidden mt-2">
          {candidates.map(candidate => {
            return (
              <div
                className={`${getCandidateStyle(candidate, id)} w-1/2 h-10 text-white font-medium text-sm flex items-center justify-center`}
                onClick={() => responding === 0 && userQuestion && selectQuestionCallback(userQuestion, userQuestionType, candidate, useExactQuestion, id)}
              >
                Ask {candidate.name}
              </div>
            )
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 md:min-w-[480px] md:max-w-[480px]">
      <div className="hidden md:flex flex-col ml-0 md:ml-36">
        <div className="text-2xl font-semibold text-blue-500">Moderator Prompts</div>
        <div className="text-sm text-slate-700 w-[336px] mt-2">The moderator will use your selection to generate a question using <span className="text-blue-500 font-medium">AI</span> based on the topic, candidate's policies, and current events. Questions don't have to be policy related. Ask candidates how they feel about pets, donuts, or anything you want!</div>
        <div className="text-sm italic text-gray-500 mt-1">Hover to ask a candidate</div>
      </div>
      <div className={`md:hidden ${responding === 0 ? "flex" : "hidden"} justify-center gap-6`}>
        {["Topic", "Question"].map(type => {
          return (
            <div
              className="rounded-full outline-blue-500 outline-2 outline p-1 w-36 flex items-center justify-center text-center text-sm font-medium"
              onClick={() => generateQuestion(type)}
            >
              Starter {type}
            </div>
          )
        })}
      </div>
      <div className="flex flex-col gap-4 items-center md:items-end">
        {getCustomQuestionCard(1)}
        {<div className="hidden md:flex flex-col gap-4 items-end">
          <div className="w-[336px] text-sm font-medium italic text-blue-500 mt-3">AI Generated Topics and Questions</div>
          {getQuestionCard("Immigration and Border Security", QuestionType.Topic, 2)}
          {getQuestionCard("What specific steps will you take to improve access to quality healthcare, reduce healthcare costs, and enhance public health infrastructure?", QuestionType.Question, 3)}
          {getQuestionCard("Economic Recovery and Job Creation", QuestionType.Topic, 4)}
          {getQuestionCard("Education and Student Debt", QuestionType.Topic, 5)}
          {getQuestionCard("How do you plan to strengthen international alliances, address global threats, and maintain national security in an increasingly interconnected world?", QuestionType.Question, 6)}
          {getQuestionCard("Climate Change and Environmental Policy", QuestionType.Topic, 7)}
          {getQuestionCard("What measures will you take to balance responsible gun ownership with public safety, especially in light of recent mass shootings?", QuestionType.Question, 8)}
        </div>}
      </div>
    </div>
  );
}